import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import { Button } from "../components/ui";
import PhotoButton from "../components/PhotoButton";
import SignOff from "../components/SignOff";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { apiGet, apiSend, API } from "../api";
import { deliveryCertFile, exportDelivery } from "../export";
import { C, R, S } from "../theme";

// Surface VALUES are kept in English for the signed PDF certificate / EHO
// compliance record; only the displayed label is localized.
const SURFACES = [
  "Grass (Staked with 380mm ground pegs - BS EN 14960)",
  "Indoors / Carpet (Sandbag weights applied)",
  "Hard Standing / Tarmac (Weighted with min 163kg per anchor point - BS EN 14960)",
];
const SURFACE_KEYS = ["handover.surfaceGrass", "handover.surfaceIndoor", "handover.surfaceHard"];
const AGREEMENTS = [
  ["agree_wind", "agreeWindTitle", "agreeWindText"],
  ["agree_supervision", "agreeSupervisionTitle", "agreeSupervisionText"],
  ["agree_no_relocation", "agreeRelocationTitle", "agreeRelocationText"],
];

const cardS = { background: C.card, borderRadius: R.md, padding: S.lg, marginBottom: S.md, border: `1px solid ${C.border}` };
const label = { color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.xs, textTransform: "uppercase", letterSpacing: 0.4 };
const inputS = { background: C.card2, borderRadius: R.md, border: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 16, padding: `0 ${S.md}px`, minHeight: 50, width: "100%", outline: "none" };
const areaS = { ...inputS, padding: S.md, minHeight: 80, resize: "vertical" };

export default function DeliveryHandover() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [a, setA] = useState(null);
  const [driverSignoff, setDriverSignoff] = useState({ signature: "", name: "", initials: "", complete: false });
  const [customerEmail, setCustomerEmail] = useState("");
  const [preNotes, setPreNotes] = useState("");
  const [surface, setSurface] = useState("");
  const [agree, setAgree] = useState({ agree_wind: false, agree_supervision: false, agree_no_relocation: false });
  const [photo, setPhoto] = useState(null);
  const [custSignoff, setCustSignoff] = useState({ signature: "", name: "", initials: "", complete: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const submitting = useRef(false);

  useEffect(() => { apiGet(`/attractions/${id}`).then(setA).catch(() => {}); }, [id]);

  const submit = async () => {
    if (submitting.current) return;
    setErr("");
    if (!driverSignoff.name.trim() || !driverSignoff.signature.trim()) return setErr(t("handover.errStaff"));
    if (!custSignoff.name.trim()) return setErr(t("handover.errCustomerName"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim())) return setErr(t("handover.errCustomerEmail"));
    if (!surface) return setErr(t("handover.errSurface"));
    if (!(agree.agree_wind && agree.agree_supervision && agree.agree_no_relocation)) return setErr(t("handover.errAgreements"));
    if (!photo) return setErr(t("handover.errPhoto"));
    if (!custSignoff.signature.trim()) return setErr(t("handover.errCustomerSig"));
    submitting.current = true; setBusy(true);
    try {
      const cert = {
        id, company: user?.company_name || "", attraction_name: a?.name || "Inflatable",
        delivered_by: driverSignoff.name.trim(), driver_name: driverSignoff.name.trim(),
        customer_name: custSignoff.name.trim(), customer_email: customerEmail.trim(),
        pre_existing_notes: preNotes.trim(), surface_type: surface,
        setup_photo: photo, driver_signature: driverSignoff.signature, customer_signature: custSignoff.signature,
        submitted_at: new Date().toISOString(),
      };
      const { base64 } = deliveryCertFile(cert);
      await apiSend(`/attractions/${id}/delivery-handovers`, "POST", {
        customer_name: cert.customer_name, customer_email: cert.customer_email, pre_existing_notes: cert.pre_existing_notes,
        surface_type: surface, agree_wind: true, agree_supervision: true, agree_no_relocation: true,
        setup_photo: photo, driver_signature: driverSignoff.signature, driver_name: cert.driver_name, driver_initials: driverSignoff.initials || "",
        customer_signature: custSignoff.signature, pdf_base64: base64, public_base: API,
      });
      setDone({ cert, email: cert.customer_email });
    } catch (e) { setErr(e?.message || t("handover.submitError")); }
    finally { submitting.current = false; setBusy(false); }
  };

  if (!a) return <div style={{ color: C.amber, textAlign: "center", paddingTop: 80 }}>{t("actions.loading")}</div>;

  if (done) {
    return (
      <div className="fade-up" style={{ padding: S.lg }}>
        <div data-testid="delivery-success" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, background: C.green, borderRadius: R.md, padding: `${S.lg}px 0`, marginBottom: S.md }}>
          <Icon name="checkmark-circle" size={28} color={C.black} /><span className="display" style={{ fontSize: 22, fontWeight: 900, color: C.black }}>{t("handover.handoverComplete")}</span>
        </div>
        <div style={cardS}>
          <div style={{ color: C.text, fontWeight: 800, fontSize: 16 }}>{done.cert.attraction_name}</div>
          <div style={{ color: C.sub, fontSize: 14, marginTop: S.sm, lineHeight: 1.5 }}>{t("handover.certGenerated")} <b style={{ color: C.text }}>{done.email}</b>. {t("handover.certSaved")}</div>
        </div>
        <button data-testid="delivery-download" className="pressable" onClick={() => exportDelivery(done.cert)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, width: "100%", minHeight: 54, borderRadius: R.md, background: C.amber, color: C.black, fontWeight: 900, fontSize: 16, border: "none", marginBottom: S.sm, cursor: "pointer" }}><Icon name="download" size={20} color={C.black} /> {t("handover.downloadCert")}</button>
        <button data-testid="delivery-done" className="pressable" onClick={() => nav(`/attraction/${id}`, { replace: true })} style={{ width: "100%", minHeight: 48, borderRadius: R.md, background: "transparent", color: C.muted, fontWeight: 800, border: "none", cursor: "pointer" }}>{t("handover.done")}</button>
        <div style={{ height: S.xxl }} />
      </div>
    );
  }

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg, paddingBottom: S.sm }}>
        <button data-testid="delivery-back" className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div style={{ flex: 1 }}>
          <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>{a.name}</div>
          <div style={{ color: C.blue, fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>{t("handover.title")}</div>
        </div>
      </div>

      <div style={{ padding: S.lg }}>
        {/* A. Staff & Customer */}
        <div style={cardS}>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.md }}>{t("handover.customerDetails")}</div>
          <div style={label}>{t("handover.customerEmail")}</div>
          <input data-testid="delivery-customer-email" type="email" inputMode="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="hirer@email.com" style={{ ...inputS, marginBottom: S.md }} />
          <div style={{ color: C.muted, fontSize: 12, marginTop: -8, marginBottom: S.md }}>{t("handover.emailHint")}</div>
          <div style={label}>{t("handover.preExisting")}</div>
          <textarea data-testid="delivery-notes" value={preNotes} onChange={(e) => setPreNotes(e.target.value)} placeholder={t("handover.preExistingPlaceholder")} style={areaS} />
        </div>

        {/* B. Setup & Anchorage */}
        <div style={cardS}>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.md }}>{t("handover.setupAnchorage")}</div>
          <div style={label}>{t("handover.surfaceType")}</div>
          <select data-testid="delivery-surface" value={surface} onChange={(e) => setSurface(e.target.value)} style={{ ...inputS, appearance: "none", WebkitAppearance: "none", background: `${C.card2} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='3'><path d='M6 9l6 6 6-6'/></svg>") no-repeat right 14px center` }}>
            <option value="" disabled>{t("handover.selectSurface")}</option>
            {SURFACES.map((s, i) => <option key={s} value={s}>{t(SURFACE_KEYS[i])}</option>)}
          </select>
        </div>

        {/* C. Mandatory agreements */}
        <div style={cardS}>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.xs }}>{t("handover.mandatoryAgreements")}</div>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: S.md }}>{t("handover.allThree")}</div>
          {AGREEMENTS.map(([key, titleKey, textKey]) => (
            <button key={key} type="button" data-testid={`agree-${key}`} onClick={() => setAgree((s) => ({ ...s, [key]: !s[key] }))} style={{ display: "flex", gap: S.md, alignItems: "flex-start", width: "100%", textAlign: "left", background: agree[key] ? "rgba(16,185,129,0.12)" : C.card2, border: `1.5px solid ${agree[key] ? C.green : C.borderStrong}`, borderRadius: R.md, padding: S.md, marginBottom: S.sm, cursor: "pointer" }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: agree[key] ? C.green : "transparent", border: `2px solid ${agree[key] ? C.green : C.borderStrong}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{agree[key] && <Icon name="checkmark-done" size={16} color={C.black} />}</div>
              <div style={{ flex: 1 }}><div style={{ color: C.text, fontWeight: 800, fontSize: 14 }}>{t(`handover.${titleKey}`)}</div><div style={{ color: C.sub, fontSize: 13, marginTop: 2, lineHeight: 1.4 }}>{t(`handover.${textKey}`)}</div></div>
            </button>
          ))}
        </div>

        {/* D. Proof photo */}
        <div style={cardS}>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.xs }}>{t("handover.proofTitle")}</div>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: S.md }}>{t("handover.proofSub")}</div>
          <PhotoButton value={photo} onCapture={setPhoto} required allowGallery testID="delivery-photo" />
        </div>

        {/* E. Legal + dual signatures */}
        <div style={{ ...cardS, border: `2px solid ${C.amber}` }}>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.sm }}>{t("handover.declaration")}</div>
          <div style={{ color: C.sub, fontSize: 13, lineHeight: 1.5, marginBottom: S.md, background: C.card2, borderRadius: R.md, padding: S.md, border: `1px solid ${C.borderStrong}` }}>{t("handover.legal")}</div>
          <SignOff title={t("handover.staffSignOff")} defaultName={user?.display_name || user?.company_name || ""} onChange={setDriverSignoff} />
          <div style={{ color: C.muted, fontSize: 12, margin: `${S.sm}px 0 ${S.md}px` }}>{t("handover.staffSignOffHint")}</div>
          <div style={{ height: 1, background: C.borderStrong, margin: `${S.md}px 0` }} />
          <SignOff title={t("handover.customerSignOff")} defaultName="" useSticky={false} persistSticky={false} onChange={setCustSignoff} />
          <div style={{ color: C.muted, fontSize: 12, marginTop: S.sm }}>{t("handover.customerSignOffHint")}</div>
        </div>

        {!!err && <div data-testid="delivery-error" style={{ color: C.red, fontWeight: 700, marginBottom: S.md, fontSize: 15 }}>{err}</div>}
        <Button title={t("handover.complete")} testID="delivery-submit" onPress={submit} loading={busy} />
        <div style={{ height: S.xxl }} />
      </div>
    </div>
  );
}
